import type { FC, ReactNode } from 'react'

import { Plus } from 'lucide-react'
import Blueprint from '@/components/site/blueprint'

export interface FaqItem {
    question: string
    answer: string
    cmsQuestion?: string
    cmsAnswer?: string
}

interface FaqListProps {
    faqs: FaqItem[]
    title?: string
    intro?: string
    id?: string
}

const FaqList: FC<FaqListProps> = ({ faqs = [], title = "Questions people ask", intro, id = 'faq' }): ReactNode => {
    if (!faqs.length) return null

    return (
        <section id={id} aria-labelledby={`${id}-heading`} className='relative isolate mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8'>
            <Blueprint kind='window' className='absolute right-0 top-0 -z-10 hidden h-64 w-[28rem] [mask-image:radial-gradient(ellipse_at_60%_45%,#000_25%,transparent_70%)] lg:block' />

            <h2 id={`${id}-heading`} className='text-3xl font-bold sm:text-4xl' data-reveal>
                {title}
            </h2>

            {intro && (
                <p className='mt-3 text-lg text-muted-foreground' data-reveal>
                    {intro}
                </p>
            )}

            <div className='mt-8 space-y-3'>
                {faqs.map((faq, index) => (
                    <details
                        key={`${faq.question}-${index}`}
                        className='faq-item group rounded-2xl border border-border bg-white shadow-sm open:border-primary/40'
                    >
                        <summary className='flex min-h-16 cursor-pointer items-center justify-between gap-4 px-5 py-4 text-lg font-bold text-navy sm:px-6'>
                            <span data-mdlfy-cms={faq.cmsQuestion}>{faq.question}</span>

                            <span className='faq-icon flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-primary transition-transform duration-300'>
                                <Plus className='size-5' aria-hidden='true' />
                            </span>
                        </summary>

                        <div className='px-5 pb-6 text-lg leading-relaxed sm:px-6'>
                            <p data-mdlfy-cms={faq.cmsAnswer}>{faq.answer}</p>
                        </div>
                    </details>
                ))}
            </div>
        </section>
    )
}

export default FaqList
